/*
 * Vue 2 compatibility adapters for account chrome supplied by the legacy
 * gd-ui-kit. The account modal keeps its legacy body, API calls, validations
 * and child settings components; this file owns only its outer chrome and the
 * narrow compatibility bridge used by customer applications.
 */

import createAccessibleModal from './accessible-modal';
import { withDomainPortalMenu } from '../../../shared/domain-portal.mjs';

const PARTNER_PROGRAM_KEY = 'frontend_baselayout_groove_partner_program';
const GsModal = createAccessibleModal();

function vnodeChildren(vnode) {
  if (!vnode) return [];
  return vnode.children || (vnode.componentOptions && vnode.componentOptions.children) || [];
}

function hasClass(vnode, className) {
  if (!vnode || !vnode.data) return false;

  const classes = [vnode.data.staticClass];
  if (typeof vnode.data.class === 'string') classes.push(vnode.data.class);

  return classes
    .filter(Boolean)
    .join(' ')
    .split(/\s+/)
    .includes(className);
}

function textFromVNode(vnode) {
  if (!vnode) return '';
  if (vnode.text) return vnode.text;

  return vnodeChildren(vnode).map(textFromVNode).join('').trim();
}

function addClass(vnode, className) {
  if (!vnode || hasClass(vnode, className)) return;
  vnode.data = vnode.data || {};
  vnode.data.staticClass = [vnode.data.staticClass, className]
    .filter(Boolean)
    .join(' ');
}

// Only the account modal's registered panels are opted in. No application
// settings route or merchant-rendered form outside this modal is selected.
const ACCOUNT_PANELS = {
  'account-domains': 'domains',
  'billing-plans': 'plans',
  'account-billing': 'billing',
  AffiliateSettings: 'affiliate',
  MyIntegrations: 'integrations',
  VendorSettings: 'vendor',
  PaymentGateway: 'gateways',
  SenderSettings: 'senders',
  VideoSettings: 'video',
};

function markAccountPanels(vnode) {
  if (!vnode) return;
  const tag = vnode.componentOptions && vnode.componentOptions.tag;
  if (ACCOUNT_PANELS[tag]) {
    addClass(vnode, 'gs-account-panel');
    addClass(vnode, `gs-account-panel--${ACCOUNT_PANELS[tag]}`);
  }
  vnodeChildren(vnode).forEach(markAccountPanels);
}

function callHandlers(handlers, event) {
  (Array.isArray(handlers) ? handlers : [handlers]).forEach(handler => {
    if (typeof handler === 'function') handler(event);
  });
}

export function createAccountTabsAdapter(LegacyTabs) {
  return {
    name: 'GrooveAccountTabsAdapter',
    extends: LegacyTabs,
    render(h) {
      const root = LegacyTabs.render.call(this, h);
      const strip = vnodeChildren(root).find(child => child.tag === 'div');
      vnodeChildren(strip).forEach(tab => {
        if (tab.tag !== 'span' || !tab.data || !tab.data.on || !tab.data.on.click) return;
        const click = tab.data.on.click;
        const keydown = tab.data.on.keydown;
        tab.data.attrs = {
          ...tab.data.attrs,
          role: 'button',
          tabindex: '0',
          'aria-pressed': tab.key === this.selected ? 'true' : 'false',
        };
        tab.data.on = {
          ...tab.data.on,
          keydown: event => {
            callHandlers(keydown, event);
            if (!event.defaultPrevented && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              callHandlers(click, event);
            }
          },
        };
      });
      return root;
    },
  };
}

function findRef(vnode, ref) {
  if (!vnode) return null;
  if (vnode.data && vnode.data.ref === ref) return vnode;

  return vnodeChildren(vnode).reduce(
    (found, child) => found || findRef(child, ref),
    null
  );
}

function findClass(vnode, className) {
  if (!vnode) return null;
  if (hasClass(vnode, className)) return vnode;

  return vnodeChildren(vnode).reduce(
    (found, child) => found || findClass(child, className),
    null
  );
}

function findComponentByProp(vnode, prop, value) {
  if (!vnode) return null;
  if (
    vnode.componentOptions &&
    vnode.componentOptions.propsData &&
    vnode.componentOptions.propsData[prop] === value
  ) {
    return vnode;
  }

  return vnodeChildren(vnode).reduce(
    (found, child) => found || findComponentByProp(child, prop, value),
    null
  );
}

function findContainerWithDirectText(vnode, text) {
  if (!vnode) return null;
  const descendant = vnodeChildren(vnode).reduce(
    (found, child) => found || findContainerWithDirectText(child, text),
    null
  );
  if (descendant) return descendant;

  if (
    vnodeChildren(vnode).some(
      child =>
        child &&
        (child.tag || child.componentOptions) &&
        textFromVNode(child) === text
    )
  ) {
    return vnode;
  }

  return null;
}

function stripLegacyBodyChrome(vnode) {
  if (!vnode || !vnode.data) return;

  // vue-loader compiles literal style attributes to staticStyle; dynamic
  // bindings use style. Handle both without touching any inner control style.
  ['staticStyle', 'style'].forEach(field => {
    const original = vnode.data[field];
    if (!original) return;
    if (typeof original === 'string') {
      vnode.data[field] = original.split(';')
        .filter(declaration => !/^(height|max-height|padding)\s*:/i.test(declaration.trim()))
        .join(';');
      return;
    }
    const style = { ...original };
    ['height', 'maxHeight', 'max-height', 'padding'].forEach(key => delete style[key]);
    vnode.data[field] = style;
  });
}

export function isPartnerProgramMenuItem(item, translatedLabel) {
  if (!item || typeof item !== 'object') return false;

  return (
    item.text === translatedLabel ||
    item.text === 'Groove Partner Program' ||
    item.text === PARTNER_PROGRAM_KEY
  );
}

export function createAccountModalAdapter(LegacyAccountModal) {
  const legacyRender = LegacyAccountModal.render;
  const components = { ...LegacyAccountModal.components };
  if (components.GdTabs && components.GdTabs.render) {
    components.GdTabs = createAccountTabsAdapter(components.GdTabs);
  }

  return {
    name: 'GrooveAccountModalAdapter',
    extends: LegacyAccountModal,
    components,
    created() {
      // v1 BaseLayout opens `$refs.tabs`; v2 exposes `$refs.tabsNew`. A live
      // getter makes either form resolve after the same parent `$nextTick`.
      if (!Object.prototype.hasOwnProperty.call(this.$refs, 'tabs')) {
        Object.defineProperty(this.$refs, 'tabs', {
          configurable: true,
          get: () => this.$refs.tabsNew,
        });
      }
    },
    render(h) {
      const legacyRoot = legacyRender ? legacyRender.call(this, h) : h('div');
      const legacyChildren = (legacyRoot.children || []).filter(Boolean);
      const legacyDialog = legacyChildren.find(
        vnode => hasClass(vnode, 'fixed') && hasClass(vnode, 'inset-0')
      );
      const passthrough = legacyChildren.filter(vnode => vnode !== legacyDialog);

      if (!this.open || !legacyDialog) {
        return h('div', { class: 'gs-account-modal-shell' }, passthrough);
      }

      const legacyHeader = (legacyDialog.children || []).find(vnode =>
        hasClass(vnode, 'p-4')
      );
      const legacyBody = (legacyDialog.children || []).find(vnode =>
        hasClass(vnode, 'overflow-y-auto')
      );
      const title =
        textFromVNode(findClass(legacyHeader, 'text-lg')) ||
        this.$t('frontend_sites_account_modal_my_account');

      if (legacyBody) {
        stripLegacyBodyChrome(legacyBody);
        addClass(legacyBody, 'gs-account-modal__legacy-body');
        addClass(findRef(legacyBody, 'tabsNew'), 'gs-account-modal__tabs');
        addClass(findRef(legacyBody, 'appSettingtabs'), 'gs-account-modal__app-tabs');
        addClass(findRef(legacyBody, 'groovesellSettingtabs'), 'gs-account-modal__app-tabs');
        markAccountPanels(legacyBody);
        addClass(
          findComponentByProp(legacyBody, 'tabs', this.generalSettingsTabs),
          'gs-account-modal__general-tabs'
        );
        addClass(
          findContainerWithDirectText(
            legacyBody,
            this.$t('frontend_sites_account_modal_api_key')
          ),
          'gs-account-modal__developer'
        );
      }

      return h('div', { class: 'gs-account-modal-shell' }, [
        ...passthrough,
        h(
          GsModal,
          {
            class: ['gs-app', 'gs-account-modal', 'gs-refresh-controls'],
            props: {
              value: this.open,
              title,
              size: 'xl',
            },
            on: {
              input: value => {
                if (!value) this.close();
              },
            },
          },
          legacyBody ? [legacyBody] : []
        ),
      ]);
    },
  };
}

/*
 * App switcher (grid-of-dots) policy.
 *
 * The legacy kit hardcodes the app list (v1 `data().apps`) or reads it from
 * brand data (v2 `computed.apps`). Neither can be edited, so the adapters
 * re-shape the list at render time: Groove.ai and Mobile App are removed and
 * a final Scale.gg Dashboard entry is appended.
 */
export const SCALEGG_DASHBOARD_URL = 'https://app.scaleplus.gg/dashboard';
export const SCALEGG_DASHBOARD_TITLE = 'Scale Plus Dashboard';

// Exact Scale Plus icon supplied for the shared app menu.
export { default as SCALEGG_LOGO } from './scale-plus-logo';
import SCALEGG_LOGO from './scale-plus-logo';

// v1 kit renders `<component :is="app.logo">` (SVG components), so it needs
// a component. Rendering the same data URI keeps one source of artwork.
export const ScaleggLogo = {
  name: 'ScaleggLogo',
  functional: true,
  render(h, context) {
    const data = context.data || {};
    return h('img', {
      ...data,
      attrs: { ...(data.attrs || {}), src: SCALEGG_LOGO, alt: '' },
    });
  },
};

const REMOVED_APP_TITLES = [/^groove\.?ai$/i, /^mobile app$/i];
const REMOVED_APP_URL_FRAGMENTS = ['groove.ai', 'groove-cm-mobile-app'];

function normalizeUrl(url) {
  return typeof url === 'string' ? url.trim().toLowerCase().replace(/\/+$/, '') : '';
}

export function isRemovedAppSwitcherEntry(app) {
  if (!app || typeof app !== 'object') return false;

  const title = typeof app.title === 'string' ? app.title.trim() : '';
  if (REMOVED_APP_TITLES.some(pattern => pattern.test(title))) return true;

  const url = normalizeUrl(app.url);
  return REMOVED_APP_URL_FRAGMENTS.some(fragment => url.includes(fragment));
}

function hasComponentLogo(app) {
  const logo = app && app.logo;
  return Boolean(logo) && (typeof logo === 'object' || typeof logo === 'function');
}

export function createScaleggDashboardEntry({ logo = SCALEGG_LOGO } = {}) {
  return {
    title: SCALEGG_DASHBOARD_TITLE,
    description: 'Your Scale Plus home',
    url: SCALEGG_DASHBOARD_URL,
    logo,
    type: ['apps', 'all'],
    target: '_self',
  };
}

/**
 * Pure: returns a new list with Groove.ai and Mobile App removed and a final
 * Scale.gg Dashboard entry appended (unless one with that url already exists).
 * The remaining entries are passed through untouched, so their translated
 * descriptions stay whatever the legacy kit produced.
 *
 * `options.logo` forces the logo value; by default it follows the shape of
 * the surrounding entries (component logos -> component, otherwise data URI).
 */
export function applyAppSwitcherPolicy(apps, options = {}) {
  const list = Array.isArray(apps) ? apps : [];
  const kept = list.filter(app => !isRemovedAppSwitcherEntry(app));
  const target = normalizeUrl(SCALEGG_DASHBOARD_URL);

  if (kept.some(app => app && normalizeUrl(app.url) === target)) return kept;

  const logo =
    options.logo !== undefined
      ? options.logo
      : kept.some(hasComponentLogo)
      ? ScaleggLogo
      : SCALEGG_LOGO;

  return [...kept, createScaleggDashboardEntry({ logo })];
}

/*
 * Builds the `data`/`computed` overrides that route a legacy component's
 * `apps` through the policy. Detection is static (does the legacy component
 * declare `computed.apps`?); the data itself is only evaluated per instance.
 * A computed named `apps` would collide with a v1 data key (Vue warns), so
 * v1 gets a data override and v2 gets a computed override, never both.
 */
function appSwitcherOverrides(LegacyComponent) {
  const legacyComputed = (LegacyComponent && LegacyComponent.computed) || {};
  const legacyData = LegacyComponent && LegacyComponent.data;

  if (typeof legacyComputed.apps === 'function') {
    const getLegacyApps = legacyComputed.apps;
    return {
      computed: {
        apps() {
          return applyAppSwitcherPolicy(getLegacyApps.call(this));
        },
      },
    };
  }

  if (typeof legacyData === 'function') {
    return {
      data() {
        const state = legacyData.call(this, this);
        if (state && Array.isArray(state.apps)) {
          state.apps = applyAppSwitcherPolicy(state.apps);
        }
        return state;
      },
    };
  }

  return {};
}

export function createNavDropdownAdapter(
  LegacyNavDropdown,
  { name = 'GrooveNavDropdownAdapter' } = {}
) {
  const overrides = appSwitcherOverrides(LegacyNavDropdown);

  return {
    name,
    extends: LegacyNavDropdown,
    ...(overrides.data ? { data: overrides.data } : {}),
    ...(overrides.computed ? { computed: overrides.computed } : {}),
  };
}

// The floating rail owns its own NavDropdown, independently of BaseLayout's
// grid menu. Adapt that registered child while preserving the sidebar behavior.
export function createSidebarAdapter(LegacySidebar) {
  const components = LegacySidebar.components || {};
  return {
    name: 'GrooveAppMenuSidebar',
    extends: LegacySidebar,
    components: {
      ...components,
      ...(components.NavDropdown
        ? { NavDropdown: createNavDropdownAdapter(components.NavDropdown) }
        : {}),
    },
  };
}

/*
 * Team & Workspaces entry for the shared avatar dropdown.
 *
 * The dropdown list itself is owned by the published @groovepages/gd-ui-kit
 * package, so this adapter is the only place the Groove applications can add an
 * item to it. The destination is the Scale Plus control plane, which owns
 * workspace membership and invitations; Groove keeps no second copy of that
 * state, so this is a link rather than a local screen.
 *
 * The label is deliberately identical to the Scale Plus dashboard so the same
 * destination never appears under two different names. One change here reaches
 * every Groove application, because they all share this adapter.
 */
export const TEAM_WORKSPACES_LABEL = 'Team & Workspaces';
export const TEAM_WORKSPACES_URL = 'https://app.scaleplus.gg/team';

function isMenuDivider(item) {
  return Boolean(item) && item.text === 'ruler';
}

export function withTeamWorkspacesMenuItem(items) {
  if (!Array.isArray(items)) return items;
  if (items.some(item => Boolean(item) && item.text === TEAM_WORKSPACES_LABEL)) {
    return items;
  }

  const entry = {
    text: TEAM_WORKSPACES_LABEL,
    callback() {
      if (typeof window !== 'undefined') {
        window.location.href = TEAM_WORKSPACES_URL;
      }
    },
  };

  // Sit directly above the divider that introduces Logout, matching the Scale
  // Plus profile menu. Append when the legacy menu has no trailing divider.
  const lastDivider = items.map(isMenuDivider).lastIndexOf(true);
  if (lastDivider === -1) return [...items, entry];

  return [...items.slice(0, lastDivider), entry, ...items.slice(lastDivider)];
}

export function createBaseLayoutAdapter({
  LegacyBaseLayout,
  LegacyAccountModal,
  AccountModal: SharedAccountModal,
  name = 'GrooveBaseLayoutAdapter',
}) {
  const legacyComputed = LegacyBaseLayout.computed || {};
  const legacyComponents = LegacyBaseLayout.components || {};
  const AccountModal = SharedAccountModal || createAccountModalAdapter(LegacyAccountModal);
  const appSwitcher = appSwitcherOverrides(LegacyBaseLayout);

  return {
    name,
    extends: LegacyBaseLayout,
    components: {
      ...legacyComponents,
      AccountModal,
    },
    ...(appSwitcher.data ? { data: appSwitcher.data } : {}),
    computed: {
      ...(appSwitcher.computed || {}),
      menu() {
        const getLegacyMenu = legacyComputed.menu;
        const items = getLegacyMenu ? getLegacyMenu.call(this) : [];
        const partnerLabel = this.$t
          ? this.$t(PARTNER_PROGRAM_KEY)
          : PARTNER_PROGRAM_KEY;
        const visible = Array.isArray(items)
          ? items.filter(item => !isPartnerProgramMenuItem(item, partnerLabel))
          : items;

        return withTeamWorkspacesMenuItem(withDomainPortalMenu(visible, {
          labels: this.$t ? [
            this.$t('frontend_baselayout_domains'),
            this.$t('frontend_baselayout_settings_domains'),
            this.$t('frontend_sites_account_modal_domains'),
          ] : [],
        }));
      },
    },
  };
}

export { PARTNER_PROGRAM_KEY };
